// File: /var/www/app2/frontend/src/components/DashboardPDFModal.tsx

import React, { useState } from "react";

interface Missionary {
  id: number;
  name: string;
  number: string;
  state?: string;
}

interface PDFModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: (filters: PDFFilterSelection) => void;
  currentFilters: {
    year: string | number;
    month: string;
    state: string;
    missionaryId: string;
  };
  missionaries: Missionary[];
  states: string[];
  isGenerating: boolean;
}

export interface PDFFilterSelection {
  year: string | number;
  month: string;
  state: string;
  missionaryId: string;
  missionaryName: string;
  useCurrentFilters: boolean;
}

const currentYear = new Date().getFullYear();

const months = [
  { value: "", label: "All Months" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const years = [2024, 2025, 2026, 2027];

function DashboardPDFModal({
  isOpen,
  onClose,
  onDownload,
  currentFilters,
  missionaries,
  states,
  isGenerating,
}: PDFModalProps) {
  const [useCurrentFilters, setUseCurrentFilters] = useState(true);
  const [customYear, setCustomYear] = useState<string | number>(currentFilters.year || currentYear);
  const [customMonth, setCustomMonth] = useState(currentFilters.month || "");
  const [customState, setCustomState] = useState(currentFilters.state || "");
  const [customMissionary, setCustomMissionary] = useState(currentFilters.missionaryId || "");

  // Filter missionaries based on selected state
  const filteredMissionaries = customState
    ? missionaries.filter((m) => m.state === customState)
    : missionaries;

  // Handle state change - reset missionary selection if the selected missionary is not in the new state
  const handleStateChange = (newState: string) => {
    setCustomState(newState);
    // Reset missionary if the currently selected one doesn't match the new state filter
    if (newState && customMissionary) {
      const selectedMissionary = missionaries.find((m) => String(m.id) === customMissionary);
      if (selectedMissionary && selectedMissionary.state !== newState) {
        setCustomMissionary("");
      }
    }
  };

  if (!isOpen) return null;

  const handleDownload = () => {
    const selectedMissionary = missionaries.find(
      (m) => String(m.id) === (useCurrentFilters ? currentFilters.missionaryId : customMissionary)
    );

    const filters: PDFFilterSelection = {
      year: useCurrentFilters ? currentFilters.year : customYear,
      month: useCurrentFilters ? currentFilters.month : customMonth,
      state: useCurrentFilters ? currentFilters.state : customState,
      missionaryId: useCurrentFilters ? currentFilters.missionaryId : customMissionary,
      missionaryName: selectedMissionary ? `${selectedMissionary.name} (${selectedMissionary.number})` : "",
      useCurrentFilters,
    };

    onDownload(filters);
  };

  const getFilterSummary = () => {
    const filters = useCurrentFilters
      ? currentFilters
      : { year: customYear, month: customMonth, state: customState, missionaryId: customMissionary };

    const parts: string[] = [];
    parts.push(`Year: ${filters.year}`);

    if (filters.month) {
      const monthName = months.find((m) => m.value === filters.month)?.label || filters.month;
      parts.push(`Month: ${monthName}`);
    }

    if (filters.state) {
      parts.push(`State: ${filters.state}`);
    }

    if (filters.missionaryId) {
      const missionary = missionaries.find((m) => String(m.id) === filters.missionaryId);
      if (missionary) {
        parts.push(`Missionary: ${missionary.name}`);
      }
    }

    return parts.join(" | ");
  };

  return (
    <div className="pdf-modal-overlay" onClick={onClose}>
      <div className="pdf-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-modal-header">
          <h3>Download Dashboard PDF</h3>
          <button className="pdf-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="pdf-modal-body">
          <p className="pdf-modal-description">
            Generate a PDF report of the dashboard statistics. You can use the current filters or
            customize the data you want to include.
          </p>

          {/* Filter Mode Selection */}
          <div className="pdf-filter-mode">
            <label className="pdf-radio-label">
              <input
                type="radio"
                name="filterMode"
                checked={useCurrentFilters}
                onChange={() => setUseCurrentFilters(true)}
              />
              <span>Use current dashboard filters</span>
            </label>
            <label className="pdf-radio-label">
              <input
                type="radio"
                name="filterMode"
                checked={!useCurrentFilters}
                onChange={() => setUseCurrentFilters(false)}
              />
              <span>Customize filters for PDF</span>
            </label>
          </div>

          {/* Custom Filters */}
          {!useCurrentFilters && (
            <div className="pdf-custom-filters">
              <div className="pdf-filter-row">
                <div className="pdf-filter-group">
                  <label>Year</label>
                  <select value={customYear} onChange={(e) => setCustomYear(e.target.value)}>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pdf-filter-group">
                  <label>Month</label>
                  <select value={customMonth} onChange={(e) => setCustomMonth(e.target.value)}>
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pdf-filter-row">
                <div className="pdf-filter-group">
                  <label>State/Region</label>
                  <select value={customState} onChange={(e) => handleStateChange(e.target.value)}>
                    <option value="">All States</option>
                    {states.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pdf-filter-group">
                  <label>Missionary</label>
                  <select
                    value={customMissionary}
                    onChange={(e) => setCustomMissionary(e.target.value)}
                  >
                    <option value="">All Missionaries{customState ? ` in ${customState}` : ""}</option>
                    {filteredMissionaries.map((missionary) => (
                      <option key={missionary.id} value={missionary.id}>
                        {missionary.name} ({missionary.number})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Filter Summary */}
          <div className="pdf-filter-summary">
            <strong>PDF will include:</strong>
            <p>{getFilterSummary()}</p>
          </div>

          {/* PDF Content Info */}
          <div className="pdf-content-info">
            <strong>Report Contents:</strong>
            <ul>
              <li>Ministry Impact Statistics</li>
              <li>Missionary Overview Summary</li>
              <li>Detailed Summary Table</li>
            </ul>
          </div>
        </div>

        <div className="pdf-modal-footer">
          <button className="pdf-btn pdf-btn-secondary" onClick={onClose} disabled={isGenerating}>
            Cancel
          </button>
          <button
            className="pdf-btn pdf-btn-primary"
            onClick={handleDownload}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : "Download PDF"}
          </button>
        </div>
      </div>

      <style>{`
        .pdf-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .pdf-modal-content {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 550px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .pdf-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e2e8f0;
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          border-radius: 12px 12px 0 0;
        }

        .pdf-modal-header h3 {
          margin: 0;
          font-size: 18px;
          color: white;
        }

        .pdf-modal-close {
          background: none;
          border: none;
          font-size: 28px;
          cursor: pointer;
          color: white;
          line-height: 1;
          padding: 0;
          opacity: 0.8;
          transition: opacity 0.2s;
        }

        .pdf-modal-close:hover {
          opacity: 1;
        }

        .pdf-modal-body {
          padding: 24px;
        }

        .pdf-modal-description {
          color: #4a5568;
          margin-bottom: 20px;
          line-height: 1.5;
        }

        .pdf-filter-mode {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
          padding: 16px;
          background: #f7fafc;
          border-radius: 8px;
        }

        .pdf-radio-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-size: 14px;
          color: #2d3748;
        }

        .pdf-radio-label input[type="radio"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .pdf-custom-filters {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 20px;
          padding: 16px;
          background: #f0f7ff;
          border-radius: 8px;
          border: 1px solid #c3dafe;
        }

        .pdf-filter-row {
          display: flex;
          gap: 16px;
        }

        .pdf-filter-group {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .pdf-filter-group label {
          font-size: 12px;
          font-weight: 600;
          color: #4a5568;
          text-transform: uppercase;
        }

        .pdf-filter-group select {
          padding: 10px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }

        .pdf-filter-group select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
        }

        .pdf-filter-summary {
          padding: 12px 16px;
          background: #ebf8ff;
          border-radius: 6px;
          margin-bottom: 16px;
          border-left: 4px solid #4299e1;
        }

        .pdf-filter-summary strong {
          display: block;
          font-size: 12px;
          color: #2c5282;
          margin-bottom: 4px;
        }

        .pdf-filter-summary p {
          margin: 0;
          font-size: 13px;
          color: #2d3748;
        }

        .pdf-content-info {
          padding: 12px 16px;
          background: #f7fafc;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
        }

        .pdf-content-info strong {
          display: block;
          font-size: 12px;
          color: #4a5568;
          margin-bottom: 8px;
        }

        .pdf-content-info ul {
          margin: 0;
          padding-left: 20px;
        }

        .pdf-content-info li {
          font-size: 13px;
          color: #718096;
          margin-bottom: 4px;
        }

        .pdf-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e2e8f0;
          background: #f7fafc;
          border-radius: 0 0 12px 12px;
        }

        .pdf-btn {
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }

        .pdf-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .pdf-btn-secondary {
          background: white;
          color: #4a5568;
          border: 1px solid #e2e8f0;
        }

        .pdf-btn-secondary:hover:not(:disabled) {
          background: #f7fafc;
        }

        .pdf-btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .pdf-btn-primary:hover:not(:disabled) {
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
          transform: translateY(-1px);
        }

        @media (max-width: 600px) {
          .pdf-filter-row {
            flex-direction: column;
          }

          .pdf-modal-content {
            width: 95%;
            margin: 10px;
          }
        }
      `}</style>
    </div>
  );
}

export default DashboardPDFModal;
